import { List, ListItem, ListItemText } from "@mui/material";
import { isEmpty } from "lodash";
import {
  ApproverType,
  HistoryEntryDetailsLabels,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import {
  AbsenceTypeHistoryEntryDetailsTypes,
  AbsenceTypeHistoryTableValue,
  EBillingCycleConfigurationType,
  EEntitledEmployeesType,
  EPresenceType,
  IAbsenceGroup,
  IAbsenceTypeHistoryTableEntry,
  IEntitledEmployeesType,
  IParentAbsenceType,
  SubmitLimitation,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import __ from "src/utils/Translations";

const formatSubmitLimitation = (
  limitation: SubmitLimitation,
  limitationType?: string
) => {
  if (!limitation || typeof limitation !== "object") return "";

  const { type, count, periodType } = limitation;

  if (type !== "RELATIVE") return String(limitation);

  const isAdvance = limitationType === "advance";
  const prefix = isAdvance ? "Następny" : "Poprzedni";
  const prefixPlural = isAdvance ? "Następne" : "Poprzednie";

  const periodLabels: Record<string, string> = {
    WEEK: count === 1 ? __("tydzień") : __("tygodnie"),
    MONTH: count === 1 ? __("miesiąc") : __("miesiące"),
    QUARTER: count === 1 ? __("kwartał") : __("kwartały"),
    HALF_YEAR: count === 1 ? __("półrocze") : __("półrocza"),
    YEAR: count === 1 ? __("rok") : __("lata"),
  };

  if (count === 0) {
    if (periodType === "MONTH") return __("Bieżący miesiąc");
    if (periodType === "YEAR") return __("Bieżący rok");
  }

  const periodLabel = periodLabels[periodType] || periodType;
  const finalPrefix =
    // eslint-disable-next-line no-nested-ternary
    count === 1 ? (periodType === "HALF_YEAR" ? prefixPlural : prefix) : count;

  return `${finalPrefix} ${periodLabel}`;
};

const formatConfigurationOption = (
  row: IAbsenceTypeHistoryTableEntry,
  value: AbsenceTypeHistoryTableValue
) => {
  if (row.configKey === "submitLimitations") {
    return formatSubmitLimitation(
      value as SubmitLimitation,
      row.submitLimitationType
    );
  }

  // eslint-disable-next-line no-nested-ternary
  return typeof value === "boolean"
    ? value
      ? __("Tak")
      : __("Nie")
    : (value as string);
};

const formatNotificationConfiguration = (value: AbsenceTypeHistoryTableValue) =>
  // eslint-disable-next-line no-nested-ternary
  Array.isArray(value)
    ? value.length > 0
      ? __("Włączone")
      : __("Wyłączone")
    : String(value);

const formatMainFieldValue = (
  row: IAbsenceTypeHistoryTableEntry,
  value: AbsenceTypeHistoryTableValue
) => {
  const formatters: Record<
    string,
    (val: AbsenceTypeHistoryTableValue) => string | JSX.Element
  > = {
    [AbsenceTypeHistoryEntryDetailsTypes.ACTIVE]: (val) =>
      val ? __("Aktywny") : __("Nieaktywny"),
    [AbsenceTypeHistoryEntryDetailsTypes.BILLING_CYCLE_DURATION]: (val) =>
      val === EBillingCycleConfigurationType.Yearly
        ? __("Rocznie")
        : __("Miesięcznie"),
    [AbsenceTypeHistoryEntryDetailsTypes.PRESENCE_TYPE]: (val) =>
      val === EPresenceType.Absence
        ? __("Nieobecność")
        : __("Status obecności"),
    [AbsenceTypeHistoryEntryDetailsTypes.ABSENCE_GROUP]: (val) =>
      (val as IAbsenceGroup)?.name || "-",
    [AbsenceTypeHistoryEntryDetailsTypes.PARENT]: (val) =>
      (val as IParentAbsenceType)?.name || "-",
    [AbsenceTypeHistoryEntryDetailsTypes.AVAILABLE_ABSENCE_PATTERNS]: (val) =>
      isEmpty(val) ? __("Brak wzorców") : (val as number[]).join(", "),
  };

  // Handle entitled employees separately due to complexity
  if (row.id === AbsenceTypeHistoryEntryDetailsTypes.ENTITLED_EMPLOYEES) {
    const entitledEmployees = value as IEntitledEmployeesType;

    if (entitledEmployees.type === EEntitledEmployeesType.All) {
      return __("Wszyscy pracownicy");
    }

    if (entitledEmployees.type === EEntitledEmployeesType.Explicit) {
      const explicitValues = [
        entitledEmployees.employmentTypes?.length > 0 && {
          label: __("Forma zatrudnienia"),
          value: entitledEmployees.employmentTypes.join(", "),
        },
        entitledEmployees.employeeGroups?.length > 0 && {
          label: __("Grupa pracownicza"),
          value: entitledEmployees.employeeGroups
            .map((group) => group.name)
            .join(", "),
        },
        entitledEmployees.organizationUnits?.length > 0 && {
          label: __("Jednostka organizacyjna"),
          value: entitledEmployees.organizationUnits
            .map((unit) => unit.name)
            .join(", "),
        },
        entitledEmployees.costPlaces?.length > 0 && {
          label: __("MPK"),
          value: entitledEmployees.costPlaces.join(", "),
        },
      ].filter(Boolean);

      return explicitValues.length > 0 ? (
        <List dense disablePadding>
          {explicitValues.map((item, index) => {
            if (item) {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <ListItem key={index} disableGutters>
                  <ListItemText
                    primary={`${item.label}: ${item.value}`}
                    slotProps={{ primary: { variant: "body2" } }}
                  />
                </ListItem>
              );
            }

            return null;
          })}
        </List>
      ) : (
        __("Wybrani pracownicy (brak szczegółów)")
      );
    }

    return __("Wybrani pracownicy");
  }

  // Handle approver types
  if (row.id === AbsenceTypeHistoryEntryDetailsTypes.APPROVER_TYPES) {
    if (isEmpty(value)) {
      return __("Akceptacja automatyczna");
    }

    const approverTypeLabels = {
      [ApproverType.IMMEDIATE_SUPERIOR]: __("Przełożony bezpośredni"),
      [ApproverType.INTERMEDIATE_SUPERIOR]: __("Przełożeni wyższego stopnia"),
      [ApproverType.AHR]: __("AHR"),
    };

    return (value as ApproverType[])
      .map((approverType) => approverTypeLabels[approverType])
      .join(", ");
  }

  const formatter = formatters[row.id];
  return formatter ? formatter(value) : (value as string);
};

export default (
  row: IAbsenceTypeHistoryTableEntry,
  value: AbsenceTypeHistoryTableValue
) => {
  // Handle sub-rows
  if (
    row.parentId === AbsenceTypeHistoryEntryDetailsTypes.CONFIGURATION_OPTIONS
  ) {
    return formatConfigurationOption(row, value);
  }

  if (
    row.parentId ===
    AbsenceTypeHistoryEntryDetailsTypes.NOTIFICATIONS_CONFIGURATION
  ) {
    return formatNotificationConfiguration(value);
  }

  // Handle main fields
  return formatMainFieldValue(row, value);
};

export const getFieldLabel = (row: IAbsenceTypeHistoryTableEntry): string => {
  const notificationGroups = {
    approverNotifications: __("Powiadomienia dla akceptanta"),
    immediateSuperiorAndApproverNotifications: __(
      "Powiadomienia dla bezpośredniego przełożonego"
    ),
    employeeNotifications: __("Powiadomienia dla wnioskującego"),
  };

  const notificationLabels = {
    // Approver notifications
    absenceRequestCreation: __("Złożenie wniosku"),
    absenceRequestEdition: __("Edycja lub poprawa wniosku"),
    // Superior notifications
    differentApproverAbsenceRequestAutoAcceptation: __(
      "Automatyczna akceptacja wniosku"
    ),
    differentApproverAbsenceRequestAcceptation: __(
      "Akceptacja przez pośredniego przełożonego lub administratora HR"
    ),
    // Employee notifications
    absenceRequestAcceptation: __("Zaakceptowany"),
    absenceRequestRejection: __("Odrzucony"),
    absenceRequestCancellation: __("Anulowany"),
    absenceRequestSentForCorrection: __("Zwrócony do poprawy"),
  };

  const configurationOptionsLabels = {
    absencesVisibleForOthers: __("Pokaż nazwę wniosku w kalendarzu"),
    billInHours: __("Udostępnij składanie wniosków godzinowych"),
    billInCalendarDays: __("Rozliczaj nieobecność w dniach kalendarzowych"),
    multipleRequestsPerDayAllowed: __(
      "Pozwól złożyć wiele wniosków jednego dnia"
    ),
    modificationAllowed: __("Pozwól edytować i anulować wniosek"),
    commentsAllowed: __("Dodaj pole komentarza"),
    justificationRequired: __("Wymagaj uzasadnienia wniosku"),
    attachmentRequired: __("Wymagaj dodania załącznika"),
    replacerRequired: __("Wymagaj wskazania zastępstwa"),
    "submitLimitations.prior": __("Liczba dni wstecz"),
    "submitLimitations.advance": __("Liczba dni do przodu"),
  };

  // Configuration options
  if (row.parentId === "configurationOptions") {
    if (row.configKey === "submitLimitations" && row.submitLimitationType) {
      const key =
        `${row.configKey}.${row.submitLimitationType}` as keyof typeof configurationOptionsLabels;
      return configurationOptionsLabels[key] || row.configKey;
    }
    return (
      configurationOptionsLabels[
        row.configKey as keyof typeof configurationOptionsLabels
      ] || row.configKey
    );
  }

  // Notification configuration
  if (row.parentId === "notificationsConfiguration") {
    const groupLabel =
      notificationGroups[row.groupKey as keyof typeof notificationGroups] ||
      row.groupKey;
    const notificationLabel =
      notificationLabels[
        row.notificationKey as keyof typeof notificationLabels
      ] || row.notificationKey;
    return `${groupLabel} - ${notificationLabel}`;
  }

  // Regular fields
  return (
    __(
      HistoryEntryDetailsLabels[
        row.id as keyof typeof HistoryEntryDetailsLabels
      ]
    ) || row.id
  );
};
